import { DragEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useFileGroundingValidation } from "../FileSelector/useFileGroundingValidation";

interface IUseFileDropZoneOptions {
  allowedFileTypes: string[];
  allowedFileSize: number;
  allowedTotalFileSize: number;
  disabled?: boolean;
}

export const useFileDropZone = ({
  allowedFileTypes,
  allowedFileSize,
  allowedTotalFileSize,
  disabled,
}: IUseFileDropZoneOptions) => {
  const { t } = useTranslation();

  // reset drag state when disabled changes
  useEffect(() => {
    if (disabled) {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    }
  }, [disabled]);

  const { addFiles, errorToastRef } = useFileGroundingValidation({
    allowedFileTypes,
    allowedFileSize,
    allowedTotalFileSize,
  });

  const overlayLabel = t(translationKeys.feature.chatbot.fileGroundings.dropFilesHere);

  // counts nested dragenter/dragleave events, since they also fire for child elements
  const dragCounterRef = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isDropAnimating, setIsDropAnimating] = useState(false);

  const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes("Files");

  const ignoreEvent = (event: DragEvent) => disabled || !hasFiles(event);

  const onDragEnter = (event: DragEvent) => {
    if (ignoreEvent(event)) {
      return;
    }

    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingOver(true);
  };

  const onDragOver = (event: DragEvent) => {
    if (ignoreEvent(event)) {
      return;
    }

    event.preventDefault();
  };

  const onDragLeave = (event: DragEvent) => {
    if (ignoreEvent(event)) {
      return;
    }

    event.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const onDrop = (event: DragEvent) => {
    if (ignoreEvent(event)) {
      return;
    }

    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);

    const files = event.dataTransfer.files;
    if (!files?.length) {
      return;
    }

    addFiles(files);

    // briefly play the drop animation, then reset so it can retrigger on the next drop
    setIsDropAnimating(true);
    setTimeout(() => setIsDropAnimating(false), 500);
  };

  return {
    dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
    isDraggingOver,
    isDropAnimating,
    errorToastRef,
    overlayLabel,
  };
};
